import React, { useMemo } from 'react';
import { Card, Select, Spin, Empty } from 'antd';
import { Line } from '@ant-design/charts';
import { trpc } from '../../lib/api';
import dayjs from 'dayjs';

interface StockHistoryChartProps {
  partId: string;
  defaultDays?: number;
}

export const StockHistoryChart: React.FC<StockHistoryChartProps> = ({
  partId,
  defaultDays = 30,
}) => {
  const [days, setDays] = React.useState(defaultDays);

  const { data, isLoading } = trpc.partStockLogs.getStockHistory.useQuery({
    partId,
    days,
  });

  type ChartDataPoint = {
    date: string;
    '在庫数': number;
    '変動': number;
    'タイプ': string;
  };

  const chartData: ChartDataPoint[] = useMemo(() => {
    if (!data?.logs) return [];

    return data.logs.map((log) => ({
      date: dayjs(log.createdAt).format('YYYY-MM-DD HH:mm'),
      在庫数: log.balanceAfter,
      変動: log.quantity,
      タイプ: log.transactionType === 'in' ? '入庫' :
              log.transactionType === 'out' ? '出庫' :
              log.transactionType === 'adjustment' ? '調整' : '棚卸',
    }));
  }, [data]);

  const config = {
    data: chartData,
    xField: 'date',
    yField: '在庫数',
    seriesField: 'タイプ',
    smooth: true,
    animation: {
      appear: {
        animation: 'path-in',
        duration: 1000,
      },
    },
    point: {
      size: 3,
      shape: 'circle',
    },
    tooltip: {
      customContent: (title: string, items: { data?: ChartDataPoint }[]) => {
        if (!items || items.length === 0) return '';
        const item = chartData.find(d => d.date === title);
        if (!item) return '';

        return `
          <div style="padding: 8px;">
            <div style="margin-bottom: 4px;"><strong>${title}</strong></div>
            <div>在庫数: ${item.在庫数}</div>
            <div>変動: ${item.変動 > 0 ? '+' : ''}${item.変動}</div>
            <div>種類: ${item.タイプ}</div>
          </div>
        `;
      },
    },
    yAxis: {
      min: 0,
      label: {
        formatter: (v: string) => `${v}`,
      },
    },
    xAxis: {
      label: {
        autoRotate: true,
        autoHide: true,
      },
    },
  };

  if (isLoading) {
    return (
      <Card title="在庫推移グラフ">
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (!data || chartData.length === 0) {
    return (
      <Card
        title="在庫推移グラフ"
        extra={
          <Select
            value={days}
            onChange={setDays}
            style={{ width: 120 }}
            options={[
              { label: '7日間', value: 7 },
              { label: '30日間', value: 30 },
              { label: '90日間', value: 90 },
              { label: '180日間', value: 180 },
            ]}
          />
        }
      >
        <Empty description="在庫変動データがありません" />
      </Card>
    );
  }

  return (
    <Card
      title={`在庫推移グラフ - ${data.part.name}`}
      extra={
        <Select
          value={days}
          onChange={setDays}
          style={{ width: 120 }}
          options={[
            { label: '7日間', value: 7 },
            { label: '30日間', value: 30 },
            { label: '90日間', value: 90 },
            { label: '180日間', value: 180 },
          ]}
        />
      }
    >
      <Line {...config} height={300} />
    </Card>
  );
};
